import { motion } from "framer-motion";
import Image from "next/image";
import { useTranslation } from "next-i18next";
import { Cta } from "@/components/Cta";

interface TransformationData {
  id: string;
  title: string;
  description: string;
  impact: string;
  stats: string;
  image?: string; // Optional image path
}

const transformations: TransformationData[] = [
  {
    id: "passive_to_intelligent",
    title: "From Passive Recording to Intelligent Analysis",
    description: "AI transforms raw video feeds into actionable intelligence, analyzing behavioral patterns and detecting anomalies in real-time across your entire operation.",
    impact: "",
    stats: "73% faster detection",
    image: "/images/transformations/dashboard_analytics.svg"
  },
  {
    id: "manual_to_automated",
    title: "From Manual Review to Automated Intelligence",
    description: "AI handles constant vigilance, filtering thousands of events to surface only genuine threats that require human attention. Your team focuses on strategic response, not screen monitoring.",
    impact: "",
    stats: "90% less monitoring",
    image: "/images/transformations/monitoring.png"
  },
  {
    id: "reactive_to_proactive",
    title: "Proactive Prevention",
    description: "Stop damage control. AI detects behavioral anomalies before they escalate into threats.",
    impact: "Predictive intelligence prevents incidents instead of just recording them.",
    stats: "Prevent before damage"
  },
  {
    id: "isolated_to_unified",
    title: "Unified Intelligence",
    description: "Transform existing cameras into an intelligent network without replacing hardware.",
    impact: "Correlate events across locations while dramatically reducing false alarms.",
    stats: "98% fewer false alarms"
  }
];

const ProblemRecognition = () => {
  const { t } = useTranslation('home');

  return (
    <motion.section
      className="pt-20 md:pt-24 bg-background"
      initial={{ opacity: 0 }}
      whileInView={{ opacity: 1 }}
      transition={{ duration: 0.8, ease: "easeOut" }}
      viewport={{ once: true, margin: "-50px" }}
    >
      <div className="container mx-auto px-6">
        {/* Section Header */}
        <motion.div
          className="text-center mb-16 md:mb-20"
          initial={{ opacity: 0, y: 30 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, ease: "easeOut", delay: 0.2 }}
          viewport={{ once: true }}
        >
          {/* Badge */}
          <motion.div
            className="inline-flex items-center justify-center mb-6"
            initial={{ opacity: 0, scale: 0.9 }}
            whileInView={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.4, delay: 0.1 }}
            viewport={{ once: true }}
          >
            <div className="relative px-6 py-2 rounded-full bg-electric-blue/10 backdrop-blur-sm light_effect_rounded_full">
              <span className="text-sm font-semibold tracking-wider uppercase text-electric-blue">
                {t('problem.badge', { defaultValue: 'The Challenge' })}
              </span>
            </div>
          </motion.div>

          <h2 className="text-4xl md:text-5xl font-bold mb-8">
            {t('problem.title')}
          </h2>
          <p className="text-xl md:text-2xl text-secondary-foreground max-w-4xl mx-auto leading-relaxed">
            {t('problem.subtitle')}
          </p>
        </motion.div>

        {/* Grid Layout - 3 columns x 2 rows with spanning items */}
        <div className="max-w-6xl px-8 mx-auto space-y-6">
          {/* Desktop Grid */}
          <div className="hidden lg:grid lg:grid-rows-2 gap-[18px]" style={{ gridTemplateColumns: '0.85fr 1fr 1fr' }}>
            {transformations.map((transformation, index) => {
              // First item spans 2 rows (left column)
              const isLeftItem = index === 0;
              // Second item spans 2 columns (top right)
              const isTopItem = index === 1;

              return (
                <motion.div
                  key={transformation.id}
                  className={`bg-container-background rounded-2xl overflow-hidden light_effect_card flex flex-col ${
                    isLeftItem ? 'lg:row-span-2' : ''
                  } ${
                    isTopItem ? 'lg:col-span-2 [&:before]:z-40' : ''
                  }`}
                  initial={{ opacity: 0, y: 30 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.1 }}
                  viewport={{ once: true, margin: "-50px" }}
                >
                  {/* Content */}
                  <div className={`h-full ${isTopItem ? 'relative' : 'p-6 xl:p-8 flex flex-col'}`}>
                    {/* Top Item - Content with image on right side */}
                    {isTopItem ? (
                      <div className="flex flex-row h-full overflow-hidden">
                        {/* Left side - Content */}
                        <div className="flex flex-col flex-1 p-6 xl:p-8 z-20">
                          {/* Title */}
                          <h3 className="text-2xl xl:text-3xl font-bold mb-4 leading-tight">
                            {t(`problem.${transformation.id}.title`)}
                          </h3>

                          {/* Description */}
                          <p className="text-base text-secondary-foreground leading-relaxed mb-6">
                            {t(`problem.${transformation.id}.description`)}
                          </p>

                          {/* Stats Badge */}
                          <div className="mt-auto">
                            <div className="inline-flex items-center px-4 py-2.5 bg-electric-blue/10 border border-electric-blue/20 rounded-lg">
                              <span className="text-sm font-semibold text-electric-blue">
                                {t(`problem.${transformation.id}.stats`)}
                              </span>
                            </div>
                          </div>
                        </div>

                        {/* Right side - Image with gradient overlay */}
                        <div className="relative flex-1 h-full overflow-hidden">
                          <div
                            className="absolute inset-0 bg-cover bg-center bg-no-repeat"
                            style={{
                              backgroundImage: `
                                linear-gradient(to left,
                                  transparent 0%,
                                  rgba(26, 26, 36, 0.05) 20%,
                                  rgba(26, 26, 36, 0.15) 40%,
                                  rgba(26, 26, 36, 0.35) 65%,
                                  rgba(26, 26, 36, 1.0) 100%
                                ),
                                url('/images/transformations/monitoring.png')
                              `,
                              backgroundSize: 'cover',
                              backgroundPosition: 'center'
                            }}
                          />
                          {/* Commented: boundingBox.png */}
                        </div>
                      </div>
                    ) : (
                      <>
                        {/* Regular layout for other items */}
                        {/* Title */}
                        <h3 className={`font-bold mb-4 leading-tight ${
                          isLeftItem ? 'text-2xl xl:text-3xl' : 'text-xl xl:text-2xl'
                        }`}>
                          {t(`problem.${transformation.id}.title`)}
                        </h3>

                        {/* Description */}
                        <p className={`text-secondary-foreground leading-relaxed mb-4 ${
                          isLeftItem ? 'text-sm xl:text-base' : 'text-base xl:text-lg'
                        }`}>
                          {t(`problem.${transformation.id}.description`)}
                        </p>

                        {/* Impact */}
                        {transformation.impact && (
                          <p className={`text-secondary-foreground/90 leading-relaxed mb-4 ${
                            isLeftItem ? 'text-sm xl:text-base' : 'text-base'
                          }`}>
                            {t(`problem.${transformation.id}.impact`)}
                          </p>
                        )}

                        {/* Image - Only for left item */}
                        {/* {isLeftItem && (
                          <div className="relative h-[200px] bg-muted/50 rounded-xl mb-6 overflow-hidden">
                            {transformation.image ? (
                              <Image
                                src={transformation.image}
                                alt={t(`problem.${transformation.id}.title`)}
                                fill
                                className="object-cover"
                              />
                            ) : (
                              <div className="flex items-center justify-center h-full">
                                <div className="text-center space-y-2 p-6">
                                  <p className="text-muted-foreground text-sm font-medium">
                                    Image Placeholder
                                  </p>
                                  <p className="text-xs text-muted-foreground/60">
                                    Visualization
                                  </p>
                                </div>
                              </div>
                            )}
                          </div>
                        )} */}

                        {/* Stats Badge */}
                        <div className="mt-auto">
                          <div className="inline-flex items-center px-4 py-2.5 bg-electric-blue/10 border border-electric-blue/20 rounded-lg">
                            <span className={`font-semibold text-electric-blue ${
                              isLeftItem ? 'text-base' : 'text-sm'
                            }`}>
                              {t(`problem.${transformation.id}.stats`)}
                            </span>
                          </div>
                        </div>
                      </>
                    )}
                  </div>
                </motion.div>
              );
            })}
          </div>

          {/* Mobile/Tablet - Simple stacked layout */}
          <div className="lg:hidden grid grid-cols-1 md:grid-cols-2 gap-[15px]">
            {transformations.map((transformation, index) => (
              <motion.div
                key={transformation.id}
                className="bg-container-background rounded-2xl overflow-hidden light_effect_card flex flex-col"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, ease: "easeOut", delay: index * 0.1 }}
                viewport={{ once: true, margin: "-50px" }}
              >
                {/* Content */}
                <div className="p-6 flex flex-col h-full">
                  {/* Title */}
                  <h3 className="text-xl md:text-2xl font-bold mb-4 leading-tight">
                    {t(`problem.${transformation.id}.title`)}
                  </h3>

                  {/* Description */}
                  <p className="text-secondary-foreground leading-relaxed mb-4 text-sm">
                    {t(`problem.${transformation.id}.description`)}
                  </p>

                  {/* Impact */}
                  {transformation.impact && (
                    <p className="text-secondary-foreground/90 leading-relaxed mb-5 text-sm">
                      {t(`problem.${transformation.id}.impact`)}
                    </p>
                  )}

                  {/* Stats Badge */}
                  <div className="mt-auto">
                    <div className="inline-flex items-center px-3 py-2 bg-electric-blue/10 border border-electric-blue/20 rounded-lg">
                      <span className="text-sm font-semibold text-electric-blue">
                        {t(`problem.${transformation.id}.stats`)}
                      </span>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <motion.div
          className="flex justify-center mt-16"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6, delay: 0.8 }}
          viewport={{ once: true }}
        >
          <Cta size="large">
            {t('problem.cta', { defaultValue: 'See how it works for you' })}
          </Cta>
        </motion.div>
      </div>
    </motion.section>
  );
};

export default ProblemRecognition;
